import axios from 'axios';
import api from './api';

/**
 * Stock Count import (Phase STK.4) — client for the STK.3 backend endpoints:
 *   POST /admin/venue/:venueId/stock-count/:countId/lines/import/preview
 *   POST /admin/venue/:venueId/stock-count/:countId/lines/import/commit
 *
 * Both accept a multipart upload (field `file`). Preview never writes; commit
 * updates only matched draft lines' countedQty/varianceQty and never posts.
 */

export type StockCountImportRowStatus =
  | 'MATCHED'
  | 'IGNORED_BLANK'
  | 'INVALID_QTY'
  | 'DUPLICATE'
  | 'OUT_OF_SCOPE';

export interface StockCountImportRow {
  rowNumber: number;
  inventoryItemId: string;
  itemName: string | null;
  expectedQty: number | null;
  physicalCount: number | null;
  varianceQty: number | null;
  status: StockCountImportRowStatus;
  message?: string;
  lineId?: string;
}

export interface StockCountImportSummary {
  totalRows: number;
  matchedRows: number;
  ignoredRows: number;
  invalidRows: number;
  duplicateRows: number;
  outOfScopeRows: number;
  /** Present on commit responses only. */
  appliedRows?: number;
}

export interface StockCountImportResult {
  summary: StockCountImportSummary;
  rows: StockCountImportRow[];
}

const IMPORT_ERROR_CODES: Record<string, string> = {
  IMPORT_NO_FILE: 'No file was selected.',
  IMPORT_INVALID_FILE_TYPE: 'File must be .xlsx or .csv.',
  IMPORT_PARSE_ERROR: 'Could not read the file. Please re-export and try again.',
  IMPORT_EMPTY_FILE: 'The file has no data rows.',
  IMPORT_MISSING_HEADERS: 'The file is missing required columns.',
  IMPORT_ROW_LIMIT_EXCEEDED: 'The file has too many rows.',
  COUNT_NOT_FOUND: 'Stock count not found.',
  COUNT_POSTED: 'This stock count is already posted and cannot be imported into.',
  VENUE_LEVEL_COUNT_BLOCKED: 'This count has no location and cannot be imported into.',
};

export function extractStockCountImportError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errObj?.['code'];
    if (typeof code === 'string' && IMPORT_ERROR_CODES[code]) {
      const missing = errObj?.['missing'];
      if (code === 'IMPORT_MISSING_HEADERS' && Array.isArray(missing)) {
        return `Missing columns: ${(missing as string[]).join(', ')}`;
      }
      return IMPORT_ERROR_CODES[code];
    }
    const msg = errObj?.['message'] ?? data?.['message'];
    if (typeof msg === 'string' && msg) return msg;
    if (err.response?.status === 413) return 'File is too large (max 10 MB).';
    if (err.response?.status === 403) return 'You do not have permission to import.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

const importBase = (venueId: string, countId: string) =>
  `/admin/venue/${venueId}/stock-count/${countId}/lines/import`;

const stockCountImportService = {
  preview: async (venueId: string, countId: string, file: File): Promise<StockCountImportResult> => {
    const fd = new FormData();
    fd.append('file', file);
    // Content-Type must be unset so the browser sets multipart/form-data; boundary=…
    const res = await api.post(`${importBase(venueId, countId)}/preview`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return res.data as StockCountImportResult;
  },

  commit: async (venueId: string, countId: string, file: File): Promise<StockCountImportResult> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`${importBase(venueId, countId)}/commit`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return res.data as StockCountImportResult;
  },
};

export default stockCountImportService;
